import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
  OnDestroy,
  TemplateRef,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IViewConfig } from './ag1-view-configuration-interfaces';

import { Popover } from 'primeng/popover';
import { ViewConfigurationService } from './ag1-view-configuration-service';
import {
  DeleteUiConfigMutation,
  DeleteUiConfigMutationVariables,
  UiConfigGQL,
  UiConfigQuery,
} from '@app/generated/graphql';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ToastService } from '@app/core/services/toast/toast.service';
import { InputModalComponent } from '../input-modal/input-modal.component';
import { ModalService } from '@app/core/services/modal/modal.service';
import { Apollo } from 'apollo-angular';
import {
  CreateUiConfigMutationVariables,
  UiConfigVisibilityEnum,
  UiConfigsQuery,
  UiConfigsQueryVariables,
  CreateUiConfigGQL,
  CreateUiConfigMutation,
  UpdateUiConfigGQL,
  UiConfig,
  UiConfigsGQL,
  UpdateUiConfigMutation,
  DeleteUiConfigGQL,
} from '@app/generated/graphql';
import { Angulartics2 } from 'angulartics2';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Ag1ListSearchComponent } from '../ag1-list-search/ag1-list-search.component';
import { FormsModule } from '@angular/forms';
@Component({
  selector: 'ag1-view-configuration',
  templateUrl: './ag1-view-configuration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Ag1ButtonComponent,
    AngularSvgIconModule,
    NgClass,
    Bind,
    Popover,
    NgTemplateOutlet,
    Button,
    Tooltip,
    Ag1ListSearchComponent,
    FormsModule,
  ],
})
export class Ag1ViewConfiguration implements OnInit, OnDestroy, OnChanges {
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);
  private apollo = inject(Apollo);
  private uiConfigsGql = inject(UiConfigsGQL);
  private uiConfigGql = inject(UiConfigGQL);
  private createUiConfigGQL = inject(CreateUiConfigGQL);
  private updateUiConfigGQL = inject(UpdateUiConfigGQL);
  private deleteUiConfigGQL = inject(DeleteUiConfigGQL);
  private angulartics2 = inject(Angulartics2);

  // subscription cleanup
  private destroy$ = new Subject<void>();

  // (Required) service used for state management
  @Input()
  viewConfigurationService: ViewConfigurationService;
  @Input()
  newConfigurationMessage: string =
    'Saving a view saves all filters (excl. Packing Week), columns and groups applied to the view.';
  @Input()
  updateConfigurationMessage: string =
    'Updating this view will also save all filters (excl. Packing Week), columns and groups currently applied on this view';

  // state
  mode: 'create' | 'update' | null = null;
  viewConfigurations: IViewConfig[] = [];
  // Used to filter the list of configurations based on the search input
  filteredViews: IViewConfig[];
  // Used to store the name of the view the user is creating
  newViewName: string;
  // Used to display the selected view name in the "dropdown"
  selectedViewName: string = 'Select a view';
  // (Required) Used to preselect a view if selectedViewId is provided
  @Input()
  selectedViewId: number | 'default' | null = null;
  selectedViewVisibility: UiConfigVisibilityEnum =
    UiConfigVisibilityEnum.Private;

  // (required) type/ name of configuration to fetch
  @Input()
  configType: string;
  // (required) used to disable the component when loading the parent
  @Input()
  disabled: boolean = false;

  // Find the menu component in order to close the menu when an action is taken
  @ViewChild('savedViewsMenu')
  savedViewsMenu!: Popover;
  // used to customize the view visibility options
  @ViewChild('viewVisibilityCustomOptions')
  viewVisibilityCustomOptions: TemplateRef<any>;
  @ViewChild('viewVisibilityCustomLabels')
  viewVisibilityCustomLabels: TemplateRef<any>;

  // (Required) data you'd like the view configuration to save
  // i.e columns, filters, groups, currency etc
  @Output() saveEvent = new EventEmitter<number>();
  // (Required) use stored data inparent componet
  // i.e updating filters, fields, groups, columns etc
  @Output() selectEvent = new EventEmitter<IViewConfig>();
  @Output() resetEvent = new EventEmitter();
  // (Required) used to inform parent that the view has been deleted and if the view was selected, the parent needs to reset the view
  @Output() deleteEvent = new EventEmitter<number>();

  constructor() {}

  ngOnInit(): void {
    if (!this.viewConfigurationService) {
      console.error('View Configuration Service is required');
      return;
    }

    this.fetchviewConfigurations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle configType first
    if (changes['configType']) {
      const configTypeChange = changes['configType'];

      // Optional: only reset on subsequent changes, not the first binding
      if (!configTypeChange.firstChange) {
        this.viewConfigurations = [];
        this.filteredViews = [];
        this.resetState();
      }

      this.fetchviewConfigurations();
    }

    // Then handle selectedViewId using the *new* value from Angular
    if (changes['selectedViewId']) {
      const newId = changes['selectedViewId'].currentValue as
        | number
        | 'default'
        | null
        | undefined;

      if (newId === undefined || newId === null || newId === 'default') {
        this.resetState();
      } else {
        this.selectedViewId = newId; // keep internal state in sync
        this.fetchViewConfiguration();
      }
    }
  }

  resetState(): void {
    this.selectedViewId = 'default';
    this.selectedViewName = 'Select a view';
  }

  get defaultState(): boolean {
    return this.selectedViewId === 'default';
  }

  fetchViewConfiguration(): void {
    if (!this.selectedViewId) {
      this.resetState();
      return;
    }

    this.apollo
      .watchQuery<UiConfigQuery>({
        query: this.uiConfigGql.document,
        variables: { id: this.selectedViewId },
      })
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          const config: UiConfig = res.data.uiConfig;
          const view: IViewConfig = this.mapConfig(config);

          this.viewSelect(view);
        },
        (err) => {
          console.log(err);
          this.toastService.showErrorToast(
            'Failed to load selected Configuration, please select another view.',
          );
          this.deleteEvent.emit(this.selectedViewId as number);
          this.resetState();
        },
      );
  }

  fetchviewConfigurations(): void {
    const variables: UiConfigsQueryVariables = {
      configType: this.configType,
    };

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    this.apollo
      .watchQuery<UiConfigsQuery>({
        query: this.uiConfigsGql.document,
        variables: variables,
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          const uiConfigs = res.data.uiConfigs;
          this.viewConfigurations = this.mapConfigData(uiConfigs).sort((a, b) =>
            collator.compare(a.viewName, b.viewName),
          );

          this.resetFilteredViews();
        },
        (err) => {
          console.log(err);
          this.toastService.showErrorToast(
            'Failed to fetch View Configurations',
          );
        },
      );
  }

  private mapConfigData(configs: UiConfig[]): IViewConfig[] {
    return configs.map((config: UiConfig) => {
      return this.mapConfig(config);
    });
  }

  private mapConfig(config: UiConfig): IViewConfig {
    return {
      ...config.data,
      id: config.id,
      displayName: `${config.data['viewName']} (${config.visibility})`,
      visibility: config.visibility,
    } as IViewConfig;
  }

  // events
  viewSelect(view: IViewConfig): void {
    this.selectedViewId = view.id;
    this.selectedViewName = view.viewName;

    // clone input in order to avoid mutation further down the line
    this.selectEvent.emit(structuredClone(view));
    this.closeSavedViewsMenu();
  }

  viewReset(): void {
    this.resetState();
    this.resetEvent.emit();
    this.closeSavedViewsMenu();
  }

  // creation
  viewCreate(): void {
    this.mode = 'create';
    this.showCreateViewModal();
    this.closeSavedViewsMenu();
  }

  onSaveAView(): void {
    this.angulartics2.eventTrack.next({
      action: 'View Configuration: Save a View',
      properties: {
        feature: this.configType,
      },
    });

    this.viewCreate();
  }

  showCreateViewModal(): InputModalComponent {
    this.saveEvent.emit();

    const modalInstance = this.modalService.showInput({
      title: 'Save your view',
      confirmBtnText: 'Save view',
      fields: [
        {
          inputType: 'text',
          inputLabel: 'View name',
          required: true,
          placeholder: 'Give your view a name',
        },
        {
          inputType: 'ng-select',
          inputLabel: 'View visibility',
          required: true,
          placeholder: 'Who can see this view?',
          ngSelectConfigs: {
            options: [
              {
                visibility: 'Private',
                heading: 'Only Me',
                description: 'No one else in your company can see this view.',
                icon: '/assets/icons/general/user.svg',
              },
              {
                visibility: 'Company',
                heading: 'My Company',
                description: 'Everyone at your company can see this view.',
                icon: '/assets/icons/general/company.svg',
              },
            ],
            bindLabel: 'visibility',
            bindValue: 'visibility',
            optionsTemplate: this.viewVisibilityCustomOptions,
            labelTemplate: this.viewVisibilityCustomLabels,
          },
        },
        {
          inputType: 'infoMessage',
          inputLabel: '',
          required: false,
          message: this.newConfigurationMessage,
        },
      ],
      onConfirm: (result) => {
        // result[0] stores the name of the view the user has entered
        const newViewName = result[0];
        // result[1] stores the views visibility selected by the user
        const newViewVisibility = result[1];

        if (newViewVisibility === 'Private') {
          this.selectedViewVisibility = UiConfigVisibilityEnum.Private;
        } else {
          this.selectedViewVisibility = UiConfigVisibilityEnum.Company;
        }

        // Check if the view name entered already exists
        if (
          this.viewConfigurations.some(
            (viewConfig) => viewConfig.viewName === newViewName,
          )
        ) {
          modalInstance.addCustomError(
            'View name',
            'This view name already exists',
          );
          return;
        }

        this.newViewName = newViewName;

        //Create the new view configuration
        this.createViewConfig();

        //Send analytics to mixpanel
        this.angulartics2.eventTrack.next({
          action: 'View Configuration: View name',
          properties: {
            feature: this.configType,
          },
        });

        this.angulartics2.eventTrack.next({
          action: 'View Configuration: View Visibility',
          properties: {
            feature: this.configType,
            viewVisibility: newViewVisibility,
          },
        });

        this.angulartics2.eventTrack.next({
          action: 'View Configuration: Save View',
          properties: {
            feature: this.configType,
          },
        });

        modalInstance.hide();
      },
    });
    return modalInstance;
  }

  createViewConfig(): void {
    this.viewConfigurationService.configurationDataToSave$
      .pipe(take(1))
      .subscribe((viewConfigurationDataToSave) => {
        if (this.mode !== 'create') {
          return;
        }

        const variables: CreateUiConfigMutationVariables = {
          attributes: {
            data: {
              // data from service
              ...viewConfigurationDataToSave,
              // name of the view
              viewName: this.newViewName,
            },
            configType: this.configType,
            visibility: this.selectedViewVisibility,
          },
        };

        this.apollo
          .mutate<CreateUiConfigMutation>({
            mutation: this.createUiConfigGQL.document,
            variables: variables,
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (res) => {
              this.fetchviewConfigurations();

              this.selectedViewId = res.data.createUiConfig.config.id;
              this.selectedViewName = this.newViewName;

              const view: IViewConfig = this.mapConfig(
                res.data.createUiConfig.config,
              );

              // clone input in order to avoid mutation further down the line
              this.selectEvent.emit(structuredClone(view));

              this.toastService.showSuccessToast('View saved successfully');

              this.mode = null;
            },
            (err) => {
              this.mode = null;
              console.log(err);
              this.toastService.showErrorToast('Failed to save View');
            },
          );
      });
  }

  // update
  viewUpdate(view: IViewConfig): void {
    this.mode = 'update';
    this.showUpdateViewModal(view);
    this.closeSavedViewsMenu();

    //send analytics to mixpanel
    this.angulartics2.eventTrack.next({
      action: 'View Configuration: Edit View',
      properties: {
        feature: this.configType,
      },
    });
  }

  showUpdateViewModal(view: IViewConfig): InputModalComponent {
    this.saveEvent.emit();

    const modalInstance = this.modalService.showInput({
      title: 'Update your view',
      confirmBtnText: 'Update view',
      markAsDirty: true,
      fields: [
        {
          inputType: 'text',
          inputLabel: 'View name',
          defaultValue: view.viewName,
          required: true,
          placeholder: view.viewName,
        },
        {
          inputType: 'ng-select',
          inputLabel: 'View visibility',
          required: true,
          defaultValue: view.visibility,
          ngSelectConfigs: {
            options: [
              {
                visibility: 'Private',
                heading: 'Only Me',
                description: 'No one else in your company can see this view.',
                icon: '/assets/icons/general/user.svg',
              },
              {
                visibility: 'Company',
                heading: 'My Company',
                description: 'Everyone at your company can see this view.',
                icon: '/assets/icons/general/company.svg',
              },
            ],
            bindLabel: 'visibility',
            bindValue: 'visibility',
            optionsTemplate: this.viewVisibilityCustomOptions,
            labelTemplate: this.viewVisibilityCustomLabels,
          },
        },
        {
          inputType: 'infoMessage',
          inputLabel: '',
          required: false,
          message: this.updateConfigurationMessage,
        },
      ],
      onConfirm: (result) => {
        //result[0] stores the name of the view the user typed
        const viewNameEntered = result[0];
        //result[1] stores the views visibility selected by the user
        const visibilityEntered = result[1];

        if (visibilityEntered === 'Private') {
          this.selectedViewVisibility = UiConfigVisibilityEnum.Private;
        } else {
          this.selectedViewVisibility = UiConfigVisibilityEnum.Company;
        }

        //Check if the entered name already exists
        if (
          this.viewConfigurations.filter(
            (viewConfig) =>
              viewConfig.viewName === viewNameEntered &&
              viewConfig.id !== view.id,
          ).length
        ) {
          modalInstance.addCustomError(
            'View name',
            'This view name already exists',
          );
          return;
        }

        this.newViewName = viewNameEntered;

        //If the name of the selected view is updated then change the view displayed in the dropdown
        if (this.selectedViewId === view.id) {
          this.selectedViewName = viewNameEntered;
        }

        this.updateViewConfig(view);

        //Send analytics to mixpanel
        this.angulartics2.eventTrack.next({
          action: 'View Configuration: View name',
          properties: {
            feature: this.configType,
          },
        });

        this.angulartics2.eventTrack.next({
          action: 'View Configuration: View Visibility',
          properties: {
            feature: this.configType,
            viewVisibility: visibilityEntered,
          },
        });

        this.angulartics2.eventTrack.next({
          action: 'View Configuration: Update View',
          properties: {
            feature: this.configType,
          },
        });

        modalInstance.hide();
      },
    });
    return modalInstance;
  }

  updateViewConfig(view: IViewConfig): void {
    this.viewConfigurationService.configurationDataToSave$
      .pipe(take(1))
      .subscribe((viewConfigurationDataToSave) => {
        if (this.mode !== 'update') {
          return;
        }

        const variables: CreateUiConfigMutationVariables = {
          attributes: {
            id: view.id,
            data: {
              // data from service
              ...viewConfigurationDataToSave,
              // name of the view
              viewName: this.newViewName,
            },
            configType: this.configType,
            visibility: this.selectedViewVisibility,
          },
        };

        this.apollo
          .mutate<UpdateUiConfigMutation>({
            mutation: this.updateUiConfigGQL.document,
            variables: variables,
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (_res) => {
              this.fetchviewConfigurations();

              this.toastService.showSuccessToast('View updated successfully');
              this.mode = null;
            },
            (err) => {
              console.log(err);
              this.mode = null;
              this.toastService.showErrorToast('Failed to update View');
            },
          );
      });
  }

  // deletion
  viewDelete(view: IViewConfig): void {
    this.showDeleteModal(view);
    this.closeSavedViewsMenu();

    this.angulartics2.eventTrack.next({
      action: 'View Configuration: Delete View',
      properties: {
        feature: this.configType,
      },
    });
  }

  showDeleteModal(view: IViewConfig): void {
    const modalInstance = this.modalService.showConfirm({
      title: 'Delete View?',
      content: `Are you sure you want to delete the view '${view.viewName}'? Deleting a view can't be undone.`,
      confirmBtnText: 'Delete View',
      confirmBtnClass: 'is-danger',
      onConfirm: () => {
        if (this.selectedViewId === view.id) {
          this.resetState();
        }

        this.deleteViewConfig(view.id);

        this.angulartics2.eventTrack.next({
          action: 'View Configuration: Delete View Confirmation',
          properties: {
            feature: this.configType,
          },
        });

        this.deleteEvent.emit(view.id);
        modalInstance.hide();
      },
    });
  }

  deleteViewConfig(viewId: number) {
    const variables: DeleteUiConfigMutationVariables = {
      deleteUiConfigId: viewId,
    };

    this.apollo
      .mutate<DeleteUiConfigMutation>({
        mutation: this.deleteUiConfigGQL.document,
        variables: variables,
      })
      .subscribe(
        (_res) => {
          this.fetchviewConfigurations();
          this.toastService.showSuccessToast('View deleted successfully');
        },
        (err) => {
          console.log(err);
          this.toastService.showErrorToast('Failed to delete View');
        },
      );
  }

  //Closing Menus
  closeSavedViewsMenu(): void {
    this.savedViewsMenu.hide();
  }

  resetFilteredViews(): void {
    this.filteredViews = this.viewConfigurations
      ? [...this.viewConfigurations]
      : [];
  }

  trackViewOpenEvent(): void {
    this.angulartics2.eventTrack.next({
      action: 'View Configuration: View configuration drop-down',
      properties: {
        feature: this.configType,
      },
    });
  }
}
